import React from 'react';
import Header from '../Shared/Header/Header';
import Footer from '../Shared/Footer/Footer';
import SubHeader from '../Shared/SubHeader';

// Privacy Policy + Terms for the website and the mobile app. Required for the
// Google Play listing (data-safety / app-content) and for handling health data.
// Plain-language baseline; not a substitute for tailored legal advice.

const UPDATED = 'June 2026';
const SUPPORT = 'support@specialisthub.co.zw';

const privacyContent = (
	<>
		<p>
			SpecialistHub (“we”, “us”) operates the website specialisthub.co.zw and the SpecialistHub
			mobile app. We act as a booking coordinator that connects patients with medical specialists
			in Zimbabwe. This policy explains what we collect, why, and your choices. Last updated: {UPDATED}.
		</p>

		<h3>Information we collect</h3>
		<ul>
			<li><strong>Booking request details</strong> you provide: your name, email address, phone number, and the reason for your visit or symptoms you choose to share.</li>
			<li><strong>Appointment preferences</strong>: the specialist, preferred date, and preferred time of day.</li>
			<li><strong>Device notification token</strong> (mobile app only): a Firebase Cloud Messaging token so we can notify you when your appointment is confirmed or rescheduled. It is linked to your booking, not to a personal account.</li>
			<li><strong>Basic technical data</strong> needed to operate the service securely (e.g. to prevent automated abuse of the booking form).</li>
		</ul>
		<p>
			We ask for the minimum needed to arrange your appointment. You do not need to create an
			account to submit a booking request.
		</p>

		<h3>How we use your information</h3>
		<ul>
			<li>To coordinate your appointment with the specialist and confirm the date, time, and fee.</li>
			<li>To contact you about your request by phone, WhatsApp, email, or push notification.</li>
			<li>To send you a booking reference and status updates.</li>
			<li>To protect the service from spam and automated abuse.</li>
		</ul>
		<p>
			Payment is arranged and settled directly with us or the practice (cash, EcoCash, or bank
			transfer). We do <strong>not</strong> collect or store card or bank details in the app or on the website.
		</p>

		<h3>How information is shared</h3>
		<ul>
			<li>With the specialist (or their practice) you ask to be booked with, so they can attend to you.</li>
			<li>With service providers that help us operate: our email provider (Brevo) for transactional email, Google Firebase for push notifications, and Cloudinary for images. These providers process data only to deliver their service to us.</li>
			<li>We do not sell your personal information.</li>
		</ul>

		<h3>Health information</h3>
		<p>
			Any health details you share (such as a reason for visit) are sensitive. We handle them
			confidentially and use them only to route your request to the right specialist and to help
			that specialist prepare for your appointment.
		</p>

		<h3>Data retention &amp; your rights</h3>
		<p>
			We keep booking information for as long as needed to provide the service and meet legal or
			record-keeping obligations, then delete or anonymise it. You may request access to, correction
			of, or deletion of your information, and you can opt out of push notifications in your device
			settings at any time. To make a request, contact us at {SUPPORT}.
		</p>

		<h3>Children</h3>
		<p>
			The service is intended for adults arranging appointments. A parent or guardian may submit a
			request on behalf of a minor in their care.
		</p>

		<h3>Contact</h3>
		<p>Questions about this policy? Email {SUPPORT}.</p>
	</>
);

const termsContent = (
	<>
		<p>
			These terms govern your use of the SpecialistHub website and mobile app. By submitting a
			booking request you agree to them. Last updated: {UPDATED}.
		</p>

		<h3>What SpecialistHub is</h3>
		<p>
			SpecialistHub is a <strong>booking coordination service</strong>. We help you request an
			appointment with a medical specialist and we coordinate the details on your behalf. We are
			not a clinic, we do not provide medical advice, diagnosis, or treatment, and we are not a
			party to the care a specialist provides.
		</p>

		<h3>Bookings are requests, not confirmed appointments</h3>
		<ul>
			<li>When you submit a booking, you are sending a <strong>request</strong>. The date and time you choose are preferences.</li>
			<li>Our team coordinates with the specialist and contacts you to confirm the actual appointment time. The confirmed time may differ from your preference.</li>
			<li>Your appointment is confirmed only after we agree the time with you and payment is arranged.</li>
		</ul>

		<h3>Payment</h3>
		<p>
			Consultation fees are set by the specialist and shown for guidance. No payment is taken online.
			Payment is arranged and settled directly with us or the practice (cash, EcoCash, or bank
			transfer). Quote your booking reference when paying.
		</p>

		<h3>Cancellations &amp; changes</h3>
		<p>
			If you need to cancel or change a request, contact us as early as possible using your booking
			reference. Specialists may also reschedule; we will inform you if that happens.
		</p>

		<h3>Acceptable use</h3>
		<ul>
			<li>Provide accurate information and use the service only to make genuine booking requests.</li>
			<li>Do not submit false, abusive, or automated requests. We use anti-abuse checks and may decline or remove requests that breach these terms.</li>
		</ul>

		<h3>Medical disclaimer</h3>
		<p>
			Content on SpecialistHub is for general information only and is not medical advice. In an
			emergency, call your local emergency services or go to the nearest hospital immediately.
		</p>

		<h3>Liability</h3>
		<p>
			We provide the coordination service with reasonable care but do not guarantee the
			availability, conduct, or outcome of any specialist’s care. To the extent permitted by law,
			SpecialistHub is not liable for the medical services provided by specialists.
		</p>

		<h3>Changes &amp; contact</h3>
		<p>
			We may update these terms; the latest version will always be available here. Questions? Email {SUPPORT}.
		</p>
	</>
);

const LegalPage = ({ type }) => {
	const isPrivacy = type === 'privacy';
	return (
		<>
			<Header />
			<SubHeader
				title={isPrivacy ? 'Privacy Policy' : 'Terms & Conditions'}
				subtitle="SpecialistHub"
			/>
			<div className="container" style={{ maxWidth: 860, padding: '3rem 1rem 5rem', lineHeight: 1.7 }}>
				{isPrivacy ? privacyContent : termsContent}
			</div>
			<Footer />
		</>
	);
};

export default LegalPage;
